/**
 * PluginError 中文化格式化工具
 *
 * 在 capability 层拦截插件抛出的 PluginError，
 * 结合 manifest.displayName 生成用户可读的中文错误信息。
 * 无需修改任何插件，所有插件零改动即可生效。
 */

// PluginError 的 code 枚举值（与 action-plugin-core 保持一致，用 duck typing 识别）
const PLUGIN_ERROR_CODES = new Set([
  'INVALID_INPUT',
  'EXECUTION_ERROR',
  'INVALID_OUTPUT',
  'INVALID_CONFIG',
  'ACTION_NOT_FOUND',
]);

/**
 * 判断是否是 PluginError（duck typing，不依赖 instanceof）
 */
function isPluginError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as any).code;
  return typeof code === 'string' && PLUGIN_ERROR_CODES.has(code);
}

/**
 * 将 PluginError 格式化为面向用户的中文消息
 * @param error - 捕获到的错误对象
 * @param displayName - 插件中文名（来自 manifest.displayName）
 * @returns 格式化后的中文消息，如果不是 PluginError 则返回 null
 */
export function formatPluginError(error: unknown, displayName?: string): string | null {
  if (!isPluginError(error)) return null;

  const err = error as any;
  const label = displayName ? `「${displayName}」` : '';

  switch (err.code) {
    case 'INVALID_INPUT':
      return `${label}插件输入校验失败: ${formatZodIssuesChinese(err.details)}`;

    case 'INVALID_OUTPUT':
      return `${label}插件输出校验失败: ${formatZodIssuesChinese(err.details)}`;

    case 'INVALID_CONFIG':
      return `${label}插件配置校验失败: ${formatZodIssuesChinese(err.details)}`;

    case 'ACTION_NOT_FOUND':
      return `${label}插件操作不存在`;

    case 'EXECUTION_ERROR': {
      // 如果插件内部有有意义的 message，透传
      const inner = err.originalError instanceof Error ? err.originalError.message : '';
      return inner
        ? `${label}插件执行失败: ${inner}`
        : `${label}插件执行失败`;
    }

    default:
      return `${label}插件执行失败`;
  }
}

/**
 * 将 ZodIssue 数组格式化为中文可读字符串
 */
export function formatZodIssuesChinese(details: unknown): string {
  if (!Array.isArray(details) || details.length === 0) return '参数格式校验出错';
  return details.map(translateZodIssue).join('；');
}

function translateZodIssue(issue: any): string {
  const field = issue.path?.length > 0 ? `「${issue.path.join('.')}」` : '';

  switch (issue.code) {
    case 'invalid_type': {
      const received = issue.received;
      if (received === 'undefined' || received === 'null') {
        return `${field}字段是必填的`;
      }
      return `${field}字段类型错误，期望${translateType(issue.expected)}`;
    }

    case 'too_small': {
      if (issue.type === 'string') {
        if (issue.minimum === 1) return `${field}字段不能为空`;
        return `${field}字段长度不能少于 ${issue.minimum} 个字符`;
      }
      if (issue.type === 'number') return `${field}字段不能小于 ${issue.minimum}`;
      if (issue.type === 'array') return `${field}字段至少需要 ${issue.minimum} 项`;
      return `${field}字段值过小`;
    }

    case 'too_big': {
      if (issue.type === 'string') return `${field}字段长度不能超过 ${issue.maximum} 个字符`;
      if (issue.type === 'number') return `${field}字段不能大于 ${issue.maximum}`;
      if (issue.type === 'array') return `${field}字段最多 ${issue.maximum} 项`;
      return `${field}字段值过大`;
    }

    case 'invalid_string': {
      if (issue.validation === 'email') return `${field}字段不是有效的邮箱地址`;
      if (issue.validation === 'url') return `${field}字段不是有效的 URL`;
      if (issue.validation === 'regex') return `${field}字段格式不正确`;
      return `${field}字段格式校验出错`;
    }

    case 'invalid_enum_value':
      return `${field}字段值不在允许的范围内`;

    case 'unrecognized_keys':
      return `包含未知字段: ${issue.keys?.join(', ')}`;

    default:
      return field ? `${field}字段格式校验出错` : '输入格式校验出错';
  }
}

function translateType(type: string): string {
  const map: Record<string, string> = {
    string: '文本',
    number: '数字',
    boolean: '布尔值',
    array: '数组',
    object: '对象',
    integer: '整数',
  };
  return map[type] || type;
}
